/**
 * Deterministic assembly for ICH M11 Section 7.1.2 —
 * Temporary Discontinuation of Trial Intervention.
 *
 * Content for "Yes" is authored only from protocol source excerpts
 * (never invented). "No" pathways use fixed statement or omit.
 */

export const TEMP_DISC_ALLOWED = {
  YES: 'yes',
  NO: 'no',
};

export const TEMP_DISC_NOT_DESCRIBED_MODE = {
  STATEMENT: 'statement',
  OMIT: 'omit',
};

export const TEMP_DISC_NA_STATEMENT =
  'No protocol-defined temporary discontinuation procedures are specified.';

export const DEFAULT_TEMPORARY_DISCONTINUATION_DECISIONS = {
  temporary_discontinuation_allowed: '',
  not_described_output_mode: '',
  protocol_source_excerpts: '',
};

function normalizeDecisions(raw) {
  return {
    ...DEFAULT_TEMPORARY_DISCONTINUATION_DECISIONS,
    ...(raw && typeof raw === 'object' ? raw : {}),
  };
}

/**
 * Build Section 7.1.2 field text from decisions (without AI).
 * For Yes + excerpts only already-authored narrative is returned via existing field;
 * this function returns N/A or empty when No pathways apply.
 */
export function assembleTemporaryDiscontinuationSection712(intake) {
  const decisions = normalizeDecisions(
    intake?.discontinuation_rules?.temporary_discontinuation_decisions
  );
  const existing = String(
    intake?.discontinuation_rules?.temporary_discontinuation_trial_intervention || ''
  ).trim();
  const warnings = [];

  if (!decisions.temporary_discontinuation_allowed) {
    warnings.push(
      'Indicate whether the protocol allows temporary discontinuation (interruption) of trial intervention.'
    );
    return {
      temporary_discontinuation_trial_intervention: existing,
      omitSection: false,
      warnings,
      complete: false,
      needsGenerateFromSource: false,
    };
  }

  if (decisions.temporary_discontinuation_allowed === TEMP_DISC_ALLOWED.NO) {
    if (!decisions.not_described_output_mode) {
      warnings.push(
        'Temporary discontinuation is not allowed / not described — choose Option A (statement) or Option B (omit section).'
      );
      return {
        temporary_discontinuation_trial_intervention: '',
        omitSection: false,
        warnings,
        complete: false,
        needsGenerateFromSource: false,
      };
    }

    if (decisions.not_described_output_mode === TEMP_DISC_NOT_DESCRIBED_MODE.OMIT) {
      return {
        temporary_discontinuation_trial_intervention: '',
        omitSection: true,
        warnings,
        complete: true,
        needsGenerateFromSource: false,
      };
    }

    return {
      temporary_discontinuation_trial_intervention: TEMP_DISC_NA_STATEMENT,
      omitSection: false,
      warnings,
      complete: true,
      needsGenerateFromSource: false,
    };
  }

  // Yes — must be generated from protocol source only
  const excerpts = String(decisions.protocol_source_excerpts || '').trim();
  if (!excerpts) {
    warnings.push(
      'Paste protocol source text that defines temporary discontinuation / interruption. Generation may use only that source.'
    );
  }

  const needsGenerateFromSource = Boolean(excerpts) && !existing;
  if (needsGenerateFromSource) {
    warnings.push(
      'Protocol excerpts are ready. Click “Generate 7.1.2 from Protocol Source” to author the section (no invented criteria).'
    );
  }

  return {
    temporary_discontinuation_trial_intervention: existing,
    omitSection: false,
    warnings,
    complete: Boolean(excerpts && existing),
    needsGenerateFromSource,
  };
}

export function shouldOmitTemporaryDiscontinuationSection(intake) {
  const decisions = normalizeDecisions(
    intake?.discontinuation_rules?.temporary_discontinuation_decisions
  );
  return (
    decisions.temporary_discontinuation_allowed === TEMP_DISC_ALLOWED.NO &&
    decisions.not_described_output_mode === TEMP_DISC_NOT_DESCRIBED_MODE.OMIT
  );
}

/**
 * Strict prompt: author 7.1.2 only from protocol excerpts.
 */
export function buildTemporaryDiscontinuation712Prompt({ protocolExcerpts }) {
  return `OBJECTIVE
Generate ICH M11-compliant Section 7.1.2 – Temporary Discontinuation of Trial Intervention.

CORE AUTHORING RULE
- NEVER invent temporary discontinuation criteria.
- NEVER infer dose interruption rules.
- NEVER create safety procedures.
- NEVER generate monitoring requirements not documented in the protocol.
- NEVER introduce protocol deviations or treatment modifications that are not explicitly described.
- ONLY use the PROTOCOL SOURCE EXCERPTS provided below.
- Do NOT use CPT templates, clinical intake guesses, or general practice.

AUTHORING INSTRUCTIONS
Generate a cohesive narrative paragraph that includes ONLY documented information from the excerpts regarding any of the following that are present:
- Circumstances requiring temporary interruption
- Investigator decision criteria
- Safety-related interruptions
- Laboratory abnormalities
- Intercurrent illness
- Surgical procedures
- Pregnancy (if interruption rather than discontinuation)
- Missed doses
- Criteria for resuming treatment
- Required assessments before restarting treatment
- Monitoring during interruption
- Relationship to dose modification (if applicable)
- Cross-references to Schedule of Activities, Dose Modification, or Safety Management sections

PARAGRAPH STYLE
Write in ICH M11 protocol style, paragraph-only, submission-ready. Adapt narrative ONLY from documented protocol content. Example tone (do not copy unless supported by excerpts):

Trial intervention may be temporarily discontinued when clinically appropriate and in accordance with protocol-defined criteria. Circumstances requiring temporary interruption may include protocol-defined adverse events, laboratory abnormalities, intercurrent illnesses, planned medical procedures, or other clinical situations requiring interruption of treatment. Participants whose trial intervention is temporarily interrupted should continue protocol-specified assessments and may resume trial intervention once the protocol-defined restart criteria have been met. The Schedule of Activities and applicable safety management sections describe the required evaluations before and during treatment interruption.

PROHIBITED
- Invent interruption durations, restart criteria, monitoring procedures, dose reduction rules, or safety assessments
- Paraphrase undocumented content
- Editorial guidance, bullet lists of inventable rules, or placeholders like [X]

FINAL OUTPUT
Return ONLY the section body paragraph(s) for 7.1.2 (plain text). Do not include the heading "7.1.2". No preamble.

PROTOCOL SOURCE EXCERPTS (sole source of truth):
---
${protocolExcerpts}
---`;
}

export function isTemporaryDiscontinuationDeterministicField(fieldName) {
  return fieldName === 'temporary_discontinuation_trial_intervention';
}
